import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext,
} from '@ngxs/store';

import { CaseMedicalDocumentType } from '../../../domain/case/model/case-medical-document-type';
import { FileUtil } from '../../../shared/util/file.util';
import {
  CasePreparationFileEntry,
  CasePreparationFilesActions,
} from './case-preparation-files.actions';

export interface CasePreparationFilesStateModel {
  byCaseId: Record<number, CasePreparationFileEntry[]>;
}

@State<CasePreparationFilesStateModel>({
  name: 'casePreparationFiles',
  defaults: {
    byCaseId: {},
  },
})
@Injectable()
export class CasePreparationFilesState {
  @Selector()
  static byCaseId(
    state: CasePreparationFilesStateModel
  ): Record<number, CasePreparationFileEntry[]> {
    return state.byCaseId;
  }

  static files(caseId: number) {
    return createSelector(
      [CasePreparationFilesState],
      (state: CasePreparationFilesStateModel): CasePreparationFileEntry[] =>
        state.byCaseId?.[caseId] ?? []
    );
  }

  @Action(CasePreparationFilesActions.SeedFromCaseDetail)
  seedFromCaseDetail(
    ctx: StateContext<CasePreparationFilesStateModel>,
    action: InstanceType<typeof CasePreparationFilesActions.SeedFromCaseDetail>
  ): void {
    const current = ctx.getState().byCaseId[action.caseId] ?? [];
    if (current.length > 0) return;

    const seeded: CasePreparationFileEntry[] = (action.documents ?? [])
      .filter(d => !!d.fileName)
      .map(d => {
        const fileName = d.fileName ?? '';
        return {
          clientId:
            d.id != null
              ? String(d.id)
              : `existing-${Math.random().toString(16).slice(2)}`,
          fileName,
          originalFileName: fileName,
          size: d.size ?? null,
          extension: FileUtil.getExtension(fileName),
          url: d.url ?? null,
          status: 'uploaded',
          progress: 100,
          canDelete: true,
          documentId: d.id ?? null,
          include: true,
          type: (d.type as CaseMedicalDocumentType) ?? null,
          documentDate: null,
        };
      });

    ctx.patchState({
      byCaseId: {
        ...ctx.getState().byCaseId,
        [action.caseId]: seeded,
      },
    });
  }

  @Action(CasePreparationFilesActions.AddLocalFile)
  addLocalFile(
    ctx: StateContext<CasePreparationFilesStateModel>,
    action: InstanceType<typeof CasePreparationFilesActions.AddLocalFile>
  ): void {
    const existing = ctx.getState().byCaseId[action.caseId] ?? [];
    ctx.patchState({
      byCaseId: {
        ...ctx.getState().byCaseId,
        [action.caseId]: [...existing, action.entry],
      },
    });
  }

  @Action(CasePreparationFilesActions.UpdateProgress)
  updateProgress(
    ctx: StateContext<CasePreparationFilesStateModel>,
    action: InstanceType<typeof CasePreparationFilesActions.UpdateProgress>
  ): void {
    this.patchEntry(ctx, action.caseId, action.clientId, {
      progress: action.progress,
    });
  }

  @Action(CasePreparationFilesActions.MarkUploaded)
  markUploaded(
    ctx: StateContext<CasePreparationFilesStateModel>,
    action: InstanceType<typeof CasePreparationFilesActions.MarkUploaded>
  ): void {
    this.patchEntry(ctx, action.caseId, action.clientId, {
      status: 'uploaded',
      progress: 100,
      canDelete: true,
      documentId: action.document.id ?? null,
      url: action.document.url ?? null,
    });
  }

  @Action(CasePreparationFilesActions.MarkError)
  markError(
    ctx: StateContext<CasePreparationFilesStateModel>,
    action: InstanceType<typeof CasePreparationFilesActions.MarkError>
  ): void {
    this.patchEntry(ctx, action.caseId, action.clientId, {
      status: 'error',
      canDelete: true,
    });
  }

  @Action(CasePreparationFilesActions.SetUploading)
  setUploading(
    ctx: StateContext<CasePreparationFilesStateModel>,
    action: InstanceType<typeof CasePreparationFilesActions.SetUploading>
  ): void {
    this.patchEntry(ctx, action.caseId, action.clientId, {
      status: 'uploading',
      progress: 0,
      canDelete: false,
      documentId: null,
      url: null,
    });
  }

  @Action(CasePreparationFilesActions.Remove)
  remove(
    ctx: StateContext<CasePreparationFilesStateModel>,
    action: InstanceType<typeof CasePreparationFilesActions.Remove>
  ): void {
    const existing = ctx.getState().byCaseId[action.caseId] ?? [];
    const next = existing.filter(e => e.clientId !== action.clientId);
    ctx.patchState({
      byCaseId: {
        ...ctx.getState().byCaseId,
        [action.caseId]: next,
      },
    });
  }

  @Action(CasePreparationFilesActions.UpdateMetadata)
  updateMetadata(
    ctx: StateContext<CasePreparationFilesStateModel>,
    action: InstanceType<typeof CasePreparationFilesActions.UpdateMetadata>
  ): void {
    this.patchEntry(ctx, action.caseId, action.clientId, action.patch);
  }

  @Action(CasePreparationFilesActions.ClearCase)
  clearCase(
    ctx: StateContext<CasePreparationFilesStateModel>,
    action: InstanceType<typeof CasePreparationFilesActions.ClearCase>
  ): void {
    const state = ctx.getState();
    if (!state.byCaseId?.[action.caseId]) return;
    const { [action.caseId]: _removed, ...rest } = state.byCaseId;
    ctx.patchState({ byCaseId: rest });
  }

  private patchEntry(
    ctx: StateContext<CasePreparationFilesStateModel>,
    caseId: number,
    clientId: string,
    patch: Partial<CasePreparationFileEntry>
  ): void {
    const existing = ctx.getState().byCaseId[caseId] ?? [];
    const next = existing.map(e =>
      e.clientId === clientId ? { ...e, ...patch } : e
    );
    ctx.patchState({
      byCaseId: {
        ...ctx.getState().byCaseId,
        [caseId]: next,
      },
    });
  }
}
